import { Box, IconButton, TextField, useTheme, useMediaQuery, CircularProgress, Typography, Tooltip, LinearProgress } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useChatClient } from './useChatClient';
import { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LinkIcon from '@mui/icons-material/Link';
import CloseIcon from '@mui/icons-material/Close';
import ReplyIcon from '@mui/icons-material/Reply';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { ChatEntityPicker, useAvailableEntityTypes } from './ChatEntityPicker';
import { useTranslation } from 'react-i18next';
import { DBUploadData, useConfig } from '../../api/api';

export const ChatMessageInput = forwardRef((props, ref) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const iconBtnSize = isMobile ? 40 : 32;
  const sendBtnSize = isMobile ? 44 : 32;
  const { t } = useTranslation();
  const config = useConfig();
  const { sendMessage, sendTypingIndicator, selectedConversation, replyToMessage, setReplyToMessage, pendingFiles, setPendingFiles } = useChatClient();
  const availableEntityTypes = useAvailableEntityTypes();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [references, setReferences] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [entityPickerOpen, setEntityPickerOpen] = useState(false);
  const [isUrgent, setIsUrgent] = useState(false);
  const fileInputRef = useRef(null);
  const canLinkEntities = availableEntityTypes.length > 0;

  const handleChange = (e) => {
    const value = e.target.value;
    setMessage(value);
    // Receiver-side throttle takes care of rate-limiting; emit immediately so the
    // typing indicator appears on the very first keystroke instead of after a pause.
    if (value.trim().length > 0) {
      sendTypingIndicator();
    }
  };

  const addFiles = useCallback((files) => {
    if (!files || files.length === 0) return;

    // Add files with preview URLs
    const newAttachments = Array.from(files).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));

    setAttachments(prev => [...prev, ...newAttachments]);
  }, []);

  // Expose addFiles method to parent via ref
  useImperativeHandle(ref, () => ({
    addFiles,
  }), [addFiles]);

  // Consume pending files from context (e.g., from drag & drop on conversation item)
  useEffect(() => {
    if (pendingFiles && pendingFiles.length > 0) {
      addFiles(pendingFiles);
      setPendingFiles(null);
    }
  }, [pendingFiles, addFiles, setPendingFiles]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    addFiles(files);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments(prev => {
      const attachment = prev[index];
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const uploadFiles = () => {
    return new Promise((resolve, reject) => {
      if (attachments.length === 0) {
        resolve([]);
        return;
      }

      const formData = new FormData();
      formData.append('fileType', 'PRIVATE');
      attachments.forEach(attachment => {
        formData.append('files[]', attachment.file);
      });

      DBUploadData({
        config,
        path: 'files',
        formData,
        onProgressChange: setUploadProgress,
        onFinished: () => setUploadProgress(null),
        onResponse: (res) => {
          // Extract file IDs from response
          // res.data is the API response body, res.data.data is the array of files
          const files = res.data?.data || res.data || [];
          const fileIds = files.map(f => f.id);
          resolve(fileIds);
        },
        onError: (err) => {
          console.error('File upload failed:', err);
          reject(err);
        },
      });
    });
  };

  const [sendError, setSendError] = useState(null);

  const handleSend = useCallback(async () => {
    if ((!message.trim() && attachments.length === 0) || isSending) return;

    const previousMessage = message;
    const previousReferences = [...references];
    const previousAttachments = [...attachments];
    const previousReplyTo = replyToMessage;
    const previousIsUrgent = isUrgent;

    setIsSending(true);
    setSendError(null);
    try {
      // Upload files first if any
      let fileIds = [];
      if (attachments.length > 0) {
        fileIds = await uploadFiles();
      }

      await sendMessage(message || '', {
        references: references.map(r => ({ type: r.type, id: r.id })),
        replyToId: replyToMessage?.id,
        attachments: fileIds,
        isUrgent,
      });
      setMessage('');
      setReferences([]);
      setAttachments([]);
      setReplyToMessage(null);
      setIsUrgent(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Restore input state so user doesn't lose their message
      setMessage(previousMessage);
      setReferences(previousReferences);
      setAttachments(previousAttachments);
      setReplyToMessage(previousReplyTo);
      setIsUrgent(previousIsUrgent);
      setSendError(t('Failed to send message. Please try again.'));
      // Auto-clear error after 5 seconds
      setTimeout(() => setSendError(null), 5000);
    } finally {
      setIsSending(false);
      setUploadProgress(null);
    }
  }, [message, references, attachments, sendMessage, isSending, replyToMessage, setReplyToMessage, config, isUrgent, t]);

  const stripHtml = (html) => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAddReference = (entity) => {
    setReferences(prev => {
      if (prev.find(r => r.type === entity.type && r.id === entity.id)) {
        return prev;
      }
      return [...prev, entity];
    });
    setEntityPickerOpen(false);
  };

  const handleRemoveReference = (index) => {
    setReferences(prev => prev.filter((_, i) => i !== index));
  };

  if (!selectedConversation) return null;

  return (
    <Box
      className="p-3"
      sx={{
        // iOS Safari: respect home indicator on devices with notch/home-bar
        paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : undefined,
      }}
    >
      {replyToMessage && (
        <Box
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg border-l-2"
          sx={{
            backgroundColor: colors.bgInput,
            borderColor: colors.info,
          }}
        >
          <ReplyIcon sx={{ fontSize: 16, color: colors.info }} />
          <Box className="flex-1 min-w-0">
            <Typography variant="caption" sx={{ color: colors.info, fontWeight: 500 }}>
              {t('Replying to')} {replyToMessage.sender?.firstName}
            </Typography>
            <Typography
              variant="body2"
              noWrap
              sx={{ color: colors.text, opacity: 0.6, fontSize: '0.8rem' }}
            >
              {stripHtml(replyToMessage.content)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setReplyToMessage(null)}
            sx={{ color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {attachments.length > 0 && (
        <Box className="flex flex-wrap gap-2 mb-2 px-1">
          {attachments.map((attachment, index) => (
            <Box
              key={index}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
              sx={{
                backgroundColor: colors.card,
                border: `1px solid ${colors.bgInput}`,
              }}
            >
              {attachment.preview ? (
                <Box
                  component="img"
                  src={attachment.preview}
                  alt={attachment.name}
                  sx={{ width: 24, height: 24, borderRadius: 1, objectFit: 'cover' }}
                />
              ) : attachment.type?.startsWith('image/') ? (
                <ImageIcon sx={{ fontSize: 18, color: colors.text, opacity: 0.6 }} />
              ) : (
                <InsertDriveFileIcon sx={{ fontSize: 18, color: colors.text, opacity: 0.6 }} />
              )}
              <Box className="flex flex-col min-w-0">
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ color: colors.text, fontWeight: 500, maxWidth: 120 }}
                >
                  {attachment.name}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.text, opacity: 0.5, fontSize: '0.65rem' }}>
                  {(attachment.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleRemoveAttachment(index)}
                sx={{ p: 0.25, color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {references.length > 0 && (
        <Box className="flex flex-wrap gap-2 mb-2 px-1">
          {references.map((ref, index) => (
            <Box
              key={`${ref.type}-${ref.id}`}
              className="flex items-center gap-1 px-2 py-1 rounded-lg"
              sx={{
                backgroundColor: colors.info + '20',
                border: `1px solid ${colors.info}40`,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.info, fontWeight: 500 }}>
                {ref.label}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleRemoveReference(index)}
                sx={{ p: 0.25, color: colors.info, '&:hover': { backgroundColor: colors.info + '20' } }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {isUrgent && (
        <Box
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg"
          sx={{
            backgroundColor: `${colors.error}20`,
            border: `1px solid ${colors.error}40`,
          }}
        >
          <PriorityHighIcon sx={{ fontSize: 18, color: colors.error }} />
          <Typography variant="caption" sx={{ color: colors.error, fontWeight: 600 }}>
            {t('This message will be sent as URGENT - Recipients will see a fullscreen notification!')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setIsUrgent(false)}
            sx={{ ml: 'auto', p: 0.25, color: colors.error }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {sendError && (
        <Box
          className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg"
          sx={{
            backgroundColor: `${colors.error}15`,
            border: `1px solid ${colors.error}40`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.error, fontWeight: 500 }}>
            {sendError}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSendError(null)}
            sx={{ ml: 'auto', p: 0.25, color: colors.error }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      )}

      {uploadProgress !== null && (
        <Box className="mb-2 px-1">
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: colors.bgInput,
              '& .MuiLinearProgress-bar': {
                backgroundColor: colors.info,
              },
            }}
          />
          <Typography variant="caption" sx={{ color: colors.text, opacity: 0.6, fontSize: '0.65rem', mt: 0.5 }}>
            {t('Uploading...')} {uploadProgress}%
          </Typography>
        </Box>
      )}

      <Box
        className="flex items-end gap-2 p-2 rounded-2xl"
        sx={{ backgroundColor: colors.bgInput }}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
        />
        <Tooltip title={t('Attach file')}>
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            sx={{ width: iconBtnSize, height: iconBtnSize, color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {canLinkEntities && (
          <Tooltip title={t('Link entity')}>
            <IconButton
              size="small"
              onClick={() => setEntityPickerOpen(true)}
              sx={{ width: iconBtnSize, height: iconBtnSize, color: colors.text, opacity: 0.5, '&:hover': { opacity: 1 } }}
            >
              <LinkIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title={isUrgent ? t('Remove urgent') : t('Mark as urgent')}>
          <IconButton
            size="small"
            onClick={() => setIsUrgent(!isUrgent)}
            sx={{
              width: iconBtnSize,
              height: iconBtnSize,
              color: isUrgent ? colors.error : colors.text,
              opacity: isUrgent ? 1 : 0.5,
              backgroundColor: isUrgent ? `${colors.error}20` : 'transparent',
              '&:hover': {
                opacity: 1,
                backgroundColor: isUrgent ? `${colors.error}30` : undefined,
              },
            }}
          >
            <PriorityHighIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder={t('Write a message...')}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              padding: '4px 8px',
              fontSize: '16px',
            },
            '& .MuiInputBase-input::placeholder': {
              opacity: 0.5,
            },
          }}
        />

        <IconButton
          onMouseDown={(e) => e.preventDefault()} // keep mobile keyboard open
          onClick={handleSend}
          disabled={(!message.trim() && attachments.length === 0) || isSending}
          size="small"
          sx={{
            backgroundColor: colors.info,
            color: '#fff',
            width: sendBtnSize,
            height: sendBtnSize,
            flexShrink: 0,
            '&:hover': { backgroundColor: colors.info, opacity: 0.9 },
            '&.Mui-disabled': { backgroundColor: colors.bgInput, color: colors.text, opacity: 0.3 },
          }}
        >
          {isSending ? (
            <CircularProgress size={isMobile ? 20 : 16} color="inherit" />
          ) : (
            <SendIcon sx={{ fontSize: isMobile ? 20 : 16 }} />
          )}
        </IconButton>
      </Box>

      <ChatEntityPicker
        open={entityPickerOpen}
        onClose={() => setEntityPickerOpen(false)}
        onSelect={handleAddReference}
      />
    </Box>
  );
});
